'use client'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { ArrowRightIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'

interface TranslatedItem {
  title: string
  sub_title: string
  feature: { text: string }[]
  link: string
}

const staticData = [
  {
    url: '/image/November2025/mceclip0_63.jpg',
    featureIcons: [
      '/image/November2025/mceclip1_30.png',
      '/image/November2025/mceclip1_30.png',
    ],
    link: '/collection/ecc-collection?size=m&scroll_to_listing=true',
  },
  {
    url: '/image/November2025/mceclip0_63.jpg',
    featureIcons: [
      '/image/November2025/mceclip1_30.png',
      '/image/November2025/mceclip1_30.png',
    ],
    link: '/collection/ecc-collection?size=l&scroll_to_listing=true',
  },
  {
    url: '/image/November2025/mceclip0_63.jpg',
    featureIcons: [
      '/image/November2025/mceclip1_30.png',
      '/image/November2025/mceclip1_30.png',
    ],
    link: '/collection/ecc-collection',
  },
  {
    url: '/image/November2025/mceclip0_63.jpg',
    featureIcons: [
      '/image/November2025/mceclip1_30.png',
      '/image/November2025/mceclip1_30.png',
    ],
    link: '/collection/ecc-collection',
  },
]

export function SustainableMaterials() {
  const t = useTranslations('ecc_collection_hardcode.sustainable_materials')
  const itemCount = Object.keys(t.raw('items')).length
  const translatedItems: TranslatedItem[] = Array.from(
    { length: itemCount },
    (_, i) => t.raw(`items.${i}`)
  )

  const sustainableData = translatedItems.map((item, index) => {
    const featureCount = Object.keys(item.feature).length
    const features = Array.from({ length: featureCount }, (_, j) => {
      return t.raw(`items.${index}.feature.${j}`)
    })

    return {
      url: staticData[index].url,
      title: item.title,
      sub_title: item.sub_title,
      feature: (features as { text: string }[]).map(
        (featureItem, featureIndex) => ({
          icon: staticData[index].featureIcons[featureIndex],
          text: featureItem.text,
        })
      ),
      link: staticData[index].link,
    }
  })

  return (
    <div className='w-full py-12 text-neutral-800 md:py-20'>
      <div className='mx-auto px-5 md:container'>
        <h2 className='mb-4 text-center text-2xl font-semibold uppercase leading-normal md:text-3xl 2xl:text-4xl'>
          {t('title')}
        </h2>

        {/* Mobile Carousel */}
        <div className='md:hidden'>
          <Carousel opts={{ align: 'start', loop: true }} className='w-full'>
            <CarouselContent className='-ml-4'>
              {sustainableData.map((item, index) => (
                <CarouselItem
                  key={index}
                  className='basis-[85%] pl-4 sm:basis-1/2'
                >
                  <div className='p-1'>
                    <SustainableCard {...item} />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>

        {/* Desktop Grid */}
        <div className='hidden md:grid md:grid-cols-4 md:gap-5'>
          {sustainableData.map((item, index) => (
            <SustainableCard key={index} {...item} />
          ))}
        </div>
      </div>
    </div>
  )
}

interface SustainableCardProps {
  url: string
  title: string
  sub_title: string
  feature: { icon: string; text: string }[]
  link: string
}

function SustainableCard({
  url,
  title,
  sub_title,
  feature,
  link,
}: SustainableCardProps) {
  const t = useTranslations('ecc_collection_hardcode.sustainable_materials')
  return (
    <Link href={link} className='bg-transparent'>
      <div className='relative mb-4 aspect-[434/502] w-full overflow-hidden rounded-lg'>
        {/* <video
          className='absolute left-0 top-0 h-full w-full object-cover'
          loop
          playsInline
          muted
          autoPlay
          aria-label={title}
        >
          <source src={url} type='video/mp4' />
        </video> */}
        <Image
          src={ImageHelper.getImageUrl(url)}
          alt={title}
          width={434}
          height={502}
          className='absolute left-0 top-0 h-full w-full object-cover'
        />
        <div className='absolute bottom-3 right-3 flex h-10 w-17 items-center justify-center rounded-full border border-white'>
          <ArrowRightIcon className='h-5 w-5 text-white' />
        </div>
      </div>
      <h3 className='font-criteria text-[15px] font-medium md:text-lg'>
        {title}
      </h3>
      <p className='mb-4 text-sm text-neutral-400 md:text-base'>{sub_title}</p>
      <div className='mb-4 border-t border-neutral-600 pt-4'>
        <p className='mb-3 text-xs font-bold uppercase text-neutral-500'>
          {t('feature')}
        </p>
        <div className='space-y-2'>
          {feature.map((f, i) => (
            <div key={i} className='flex items-center gap-3'>
              <div className='h-6 w-6 md:h-8 md:w-8'>
                <Image
                  src={ImageHelper.getImageUrl(f.icon)}
                  alt={f.text}
                  width={24}
                  height={24}
                  className='h-6 w-6 md:h-8 md:w-8'
                />
              </div>
              <span className='text-sm text-neutral-600 md:text-base'>
                {f.text}
              </span>
            </div>
          ))}
        </div>
      </div>
      {/* <div className='border-t border-gray-700 pt-4'>
        <p className='mb-3 text-xs font-bold uppercase text-gray-400'>
          Tính năng
        </p>
        <div className='flex items-center gap-3'>
          <span className='text-sm text-neutral-50 md:text-base'>
            {feature}
          </span>
        </div>
      </div> */}
    </Link>
  )
}
